import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/auth/Login';
import AdminDashboard from './components/admin/AdminDashboard';
import UserDashboard from './components/user/UserDashboard';
import UserManagement from './components/admin/UserManagement';
import LeadsList from './components/leads/LeadsList';
import LeadForm from './components/leads/LeadForm';
import LeadDetailView from './components/leads/LeadDetailView';
import ProjectsList from './components/projects/ProjectsList';
import ArchivedList from './components/archived/ArchivedList';
import NotificationList from './components/notifications/NotificationList';
import BlueSkyLeads from './components/leads/BlueSkyLeads';
import ProposalPhaseLeads from './components/leads/ProposalPhaseLeads';
import NegotiationLeads from './components/leads/NegotiationLeads';

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole }) => {
  const token = localStorage.getItem('token');
  const userStr = localStorage.getItem('user');
  
  if (!token || !userStr) {
    return <Navigate to="/login" replace />;
  }
  
  try {
    const user = JSON.parse(userStr);
    
    // If specific role required, check it
    if (requiredRole && user.role !== requiredRole) {
      return <Navigate to="/" replace />;
    }
    
    return children;
  } catch {
    localStorage.clear();
    return <Navigate to="/login" replace />;
  }
};

// Dashboard Route - shows different dashboard based on role
const DashboardRoute = () => {
  const userStr = localStorage.getItem('user');
  
  try {
    const user = JSON.parse(userStr);
    
    // Administrators see AdminDashboard
    if (user.role === 'administrator') {
      return <AdminDashboard />;
    }
    
    // Users (directors/consultants) see UserDashboard
    return <UserDashboard />;
  } catch {
    return <Navigate to="/login" replace />;
  }
};

function App() {
  return (
    <Router>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<Login />} />
        
        {/* Protected Routes - All authenticated users */}
        <Route 
          path="/" 
          element={
            <ProtectedRoute>
              <DashboardRoute />
            </ProtectedRoute>
          } 
        />
        
        {/* Admin-only Routes */}
        <Route 
          path="/admin/dashboard" 
          element={
            <ProtectedRoute requiredRole="administrator">
              <AdminDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/users" 
          element={
            <ProtectedRoute requiredRole="administrator">
              <UserManagement />
            </ProtectedRoute>
          } 
        />
        
        {/* User (Director/Consultant) Routes */}
        <Route 
          path="/user/dashboard" 
          element={
            <ProtectedRoute requiredRole="user">
              <UserDashboard />
            </ProtectedRoute>
          } 
        />
        
        {/* Leads Routes - Accessible to ALL authenticated users */}
        <Route 
          path="/admin/leads" 
          element={
            <ProtectedRoute>
              <LeadsList />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/leads/new" 
          element={
            <ProtectedRoute>
              <LeadForm />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/leads/view/:id" 
          element={
            <ProtectedRoute>
              <LeadDetailView />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/leads/edit/:id" 
          element={
            <ProtectedRoute>
              <LeadForm />
            </ProtectedRoute>
          } 
        />

        {/* Lead Categories Routes - Accessible to ALL authenticated users */}
        <Route 
          path="/leads/blue-sky" 
          element={
            <ProtectedRoute>
              <BlueSkyLeads />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/leads/proposal-phase" 
          element={
            <ProtectedRoute>
              <ProposalPhaseLeads />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/leads/negotiation" 
          element={
            <ProtectedRoute>
              <NegotiationLeads />
            </ProtectedRoute>
          } 
        />

        {/* Projects Routes - Accessible to ALL authenticated users */}
        <Route 
          path="/projects" 
          element={
            <ProtectedRoute>
              <ProjectsList />
            </ProtectedRoute>
          } 
        />

        {/* Archived Routes - Accessible to ALL authenticated users */}
        <Route 
          path="/archived" 
          element={
            <ProtectedRoute>
              <ArchivedList />
            </ProtectedRoute>
          } 
        />

        {/* Notifications Routes - Accessible to ALL authenticated users */}
        <Route 
          path="/notifications" 
          element={
            <ProtectedRoute>
              <NotificationList />
            </ProtectedRoute>
          } 
        />

        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;